const installations = new WeakMap()

/**
 * Prevent an already-running Livewire response from morphing the page that
 * wire:navigate is tearing down.
 *
 * Livewire destroys Alpine's scopes before replacing the old body. A response
 * that finishes after that point still owns its old Component instance and can
 * otherwise call Alpine.morph() with the now scope-less DOM. Alpine's clone
 * pass then evaluates bindings such as `selected`, `search`, or app-provided
 * state without their former x-data stack and reports unhandled ReferenceErrors.
 *
 * Do not cancel the request: its server-side work may be meaningful. We only
 * suppress a DOM morph that can no longer be rendered safely. Components
 * inside x-persist retain their Alpine tree across navigation and are therefore
 * deliberately allowed to keep morphing.
 */
export default function installNavigateMorphGuard(Livewire, eventTarget = globalThis.document) {
  if (!Livewire?.hook || !eventTarget?.addEventListener) return null

  if (installations.has(eventTarget)) {
    return installations.get(eventTarget)
  }

  let navigating = false

  const onNavigating = () => {
    navigating = true
  }

  const onNavigated = () => {
    navigating = false
  }

  eventTarget.addEventListener('livewire:navigating', onNavigating)
  eventTarget.addEventListener('livewire:navigated', onNavigated)

  const removeMorphHook = Livewire.hook('morph.updating', ({ component, skip }) => {
    const root = component?.el
    const persisted = root?._x_wasPersisted === true || root?.closest?.('[x-persist]') != null

    if (!persisted && (navigating || !root?.isConnected)) {
      skip()
    }
  })

  const dispose = () => {
    eventTarget.removeEventListener('livewire:navigating', onNavigating)
    eventTarget.removeEventListener('livewire:navigated', onNavigated)
    removeMorphHook?.()
    installations.delete(eventTarget)
  }

  installations.set(eventTarget, dispose)

  return dispose
}
